/**
 * Canonical user-role vocabulary — SINGLE SOURCE OF TRUTH.
 *
 * This is the set of values a `users.role` column can hold, and therefore the
 * only set `@Roles()` may name. Anything that stores, transmits, validates or
 * authorises against a role MUST derive from this list rather than re-declare
 * it (AGENTS.md rules #1 and #7c).
 *
 * ## Why this lives in shared-types
 *
 * `@autopia/shared-types` has no dependencies. `@autopia/auth-guard` pulls in
 * NestJS and `@autopia/api-contracts` pulls in Zod, so both can derive from
 * this package but neither can be derived from. Putting the vocabulary at the
 * root of the dependency graph is what lets every other layer import it
 * instead of copying it.
 *
 * ## Order and membership
 *
 * The members and their order mirror `enum UserRole` in
 * `services/identity-service/prisma/schema.prisma:95-106` exactly. Prisma owns
 * the column, so Prisma is authoritative. Under database-per-service a shared
 * package cannot import a service's generated client, so this mirror is
 * forced — which is the one case AGENTS.md rule #7c permits, and it carries a
 * parity test (`user-role.spec.ts`) that reads the schema and fails on drift.
 *
 * ## What is deliberately NOT here
 *
 * - `SERVICE` — a synthetic principal minted by `JwtAuthGuard` for
 *   service-to-service calls authenticated by shared secret. It is never
 *   stored in a database and is not a role a user can hold, so it belongs to
 *   the guard, not to this vocabulary. See `SYNTHETIC_ROLES` in
 *   `packages/auth-guard/src/types.ts`.
 * - `FINANCE`, `REVENUE_MANAGER`, `SYSTEM` — named by `@Roles()` at five call
 *   sites but present in no schema and held by no account. They were never
 *   roles; they were typos that the untyped decorator could not reject. See
 *   issue #753.
 * - Ordering as authority. These values carry no rank. Whether one role
 *   outranks another is an authorisation policy question owned by
 *   `RolesGuard`, and encoding it here would invite exactly the confusion that
 *   let a device identity outrank SUPER_ADMIN.
 *
 * ## Monolith keys deliberately not ported (#958)
 *
 * The ten gate roles below came from the monolith's role table. The rest of
 * that table is intentionally absent, because this repo already expresses the
 * same authority with a role it has:
 *
 * - `subadmin` — split here into `MANAGER` and `SUPERVISOR`.
 * - `cashier` — `SALES`.
 * - `employee` — `STAFF`.
 * - `middleware` — the synthetic `SERVICE` principal, above.
 * - `guest`, `visitor` — `CUSTOMER`.
 *
 * And these are retired in the monolith itself, so they must never be ported
 * even if an old export or fixture still names one: `manless_in`,
 * `manless_out`, `gate_officer`, `turnstile`, `turnstile_in`, `turnstile_out`.
 */
export const USER_ROLES = [
  'CUSTOMER',
  'STAFF',
  'SALES',
  'GATE_OPERATOR',
  'SUPERVISOR',
  'MANAGER',
  'ADMIN',
  'SUPER_ADMIN',
  'RESELLER_PARTNER',
  'KIOSK',
  'MAIN_GATE_VALIDATOR_IN',
  'MAIN_GATE_VALIDATOR_OUT',
  'ATTRACTION_VALIDATOR_IN',
  'ATTRACTION_VALIDATOR_OUT',
  'ZONE_VALIDATOR_IN',
  'ZONE_VALIDATOR_OUT',
  'GATE_SUPERVISOR',
  'MAIN_GATE_SUPERVISOR',
  'ATTRACTION_GATE_SUPERVISOR',
  'OPERATIONS_SUPERVISOR',
] as const;

/** Union of every role a user account can hold. */
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Member access for the same vocabulary, so call sites can write
 * `UserRole.ADMIN` instead of the bare string `'ADMIN'` and get a compile
 * error on a typo.
 *
 * Derived from {@link USER_ROLES} rather than written out a second time — the
 * two cannot drift because there is only one list. The name is shared with the
 * type above deliberately: TypeScript keeps values and types in separate
 * namespaces, so `UserRole.ADMIN` (value) and `role: UserRole` (type) both
 * resolve, which is what an `enum` gave us without an `enum`'s costs.
 */
export const UserRole = Object.freeze(
  Object.fromEntries(USER_ROLES.map((role) => [role, role])),
) as { readonly [K in UserRole]: K };

/**
 * Type guard narrowing an arbitrary string to a known role.
 *
 * Use this at trust boundaries — a JWT claim, a query parameter, a webhook
 * body. An unrecognised value must be treated as "not a role", never as a role
 * that happens to be unknown.
 */
export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as readonly string[]).includes(value);
}
